import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { EntityPermissionRoleService } from '@shared-lib/services/entity-permission-role.service';
import { RouteAccessAuthorizer } from '@shared-lib/services/route-access.token';
import { UserModuleAccessService } from '@shared-lib/services/user-module-access.service';
import { environment } from '../../../environments/environment';
import {
  getMappedRoleForUser,
  UserIdentity,
} from '../../../../public/assets/dashboard-config/role-map/role-map';
import {
  normalizeRoute,
  RoleMenu,
} from '../../../../public/assets/dashboard-config/role-map/user-role';

interface ParsedIdentityToken {
  email?: string;
  preferred_username?: string;
  upn?: string;
}

const ROUTE_MODULE_KEYS: Record<string, string> = {
  '/approvals': 'approvals',
  '/approval-workflows': 'approvals',
  '/price-recommendation': 'price-recommendation',
  '/user-access': 'user-access',
  '/discount-templates': 'discount-templates',
  '/discount-request': 'discount-templates',
  '/domain-catalogs': 'item-catalogs',
  '/customer-catalogs': 'customer-catalogs',
  '/price-list-catalogs': 'price-list-catalogs',
};

const PUBLIC_AUTHORIZED_ROUTES = new Set(['/access-denied']);
const LANDING_ROUTES = new Set(['/home-main', '/spriced-home']);

@Injectable({ providedIn: 'root' })
export class KeycloakInitService implements RouteAccessAuthorizer {
  private permissionsLoaded = false;
  private permissionsLoadPromise: Promise<void> | null = null;
  private authenticated = false;
  private userEmail = '';
  private userRole: string | null = null;
  private allowedRoutes: string[] = [];

  constructor(
    private readonly keycloakService: KeycloakService,
    private readonly http: HttpClient,
    private readonly userModuleAccess: UserModuleAccessService,
    private readonly entityPermissionRole: EntityPermissionRoleService,
  ) {}

  async initKeycloak(): Promise<void> {
    this.clearAccessCache();
    try {
      await this.keycloakService.init({
        config: {
          url: environment.KEYCLOAK_URL,
          realm: environment.KEYCLOAK_REALM,
          clientId: environment.KEYCLOAK_CLIENT_ID,
        },
        initOptions: {
          onLoad: 'check-sso',
          checkLoginIframe: false,
          silentCheckSsoRedirectUri:
            window.location.origin + '/assets/silent-check-sso.html',
          pkceMethod: 'S256',
        },
        // The application interceptor is the single bearer-token mechanism.
        enableBearerInterceptor: false,
      });

      this.authenticated = this.keycloakService.isLoggedIn();
      if (!this.authenticated) return;

      await this.resolveAuthenticatedAccess();
      await this.loadAuthenticatedUserPermissions();
    } catch (error) {
      this.clearAccessCache();
      console.error('Keycloak initialization failed');
      throw error;
    }
  }

  isAuthenticated(): boolean {
    return this.authenticated && this.keycloakService.isLoggedIn();
  }

  getUserEmail(): string {
    return this.userEmail;
  }

  getUserRole(): string | null {
    return this.userRole;
  }

  getAllowedRoutes(): readonly string[] {
    return this.allowedRoutes.filter((route) => this.isModulePermitted(route));
  }

  canAccessRoute(url: string): boolean {
    const route = normalizeRoute(url);
    if (PUBLIC_AUTHORIZED_ROUTES.has(route)) return true;
    if (!this.isAuthenticated() || !this.userRole) return false;
    return this.allowedRoutes.includes(route) && this.isModulePermitted(route);
  }

  getFirstAllowedRoute(): string | null {
    return this.getAllowedRoutes()[0] ?? null;
  }

  async logout(): Promise<void> {
    this.clearAccessCache();
    await this.keycloakService.logout(window.location.origin);
  }

  loadAuthenticatedUserPermissions(): Promise<void> {
    if (this.permissionsLoaded) return Promise.resolve();
    if (this.permissionsLoadPromise) return this.permissionsLoadPromise;

    this.permissionsLoadPromise = this.fetchAuthenticatedUserPermissions()
      .then((loaded) => {
        this.permissionsLoaded = loaded;
      })
      .finally(() => {
        this.permissionsLoadPromise = null;
      });
    return this.permissionsLoadPromise;
  }

  private async resolveAuthenticatedAccess(): Promise<void> {
    const claims = this.keycloakService.getKeycloakInstance()
      .tokenParsed as ParsedIdentityToken | undefined;
    let email = String(claims?.email ?? '').trim();

    if (!email) {
      try {
        const profile = await this.keycloakService.loadUserProfile();
        email = String(profile?.email ?? '').trim();
      } catch {
        // A missing profile must not weaken authorization.
      }
    }

    const username = String(claims?.preferred_username ?? claims?.upn ?? '').trim();
    const identity: UserIdentity = { email, wwid: username, username };
    const role = getMappedRoleForUser(identity);

    this.userEmail = email;
    this.userRole = role;
    this.allowedRoutes = role
      ? (RoleMenu[role] ?? []).map(normalizeRoute).filter((route) => route !== '/')
      : [];
  }

  private async fetchAuthenticatedUserPermissions(): Promise<boolean> {
    this.userModuleAccess.clear();
    const role = this.entityPermissionRole.getLoggedInRole();
    if (!role) return false;

    const baseUrl = environment.URL.replace(/\/$/, '');
    try {
      const response = await firstValueFrom(
        this.http.post<unknown>(
          `${baseUrl}/spriced/auth/getEntityPermissions`,
          { userRole: role },
        ),
      );
      this.userModuleAccess.setFromPermissionResponse(
        response,
        role.trim().toLowerCase() === 'admin',
      );
      return true;
    } catch {
      this.userModuleAccess.clear();
      console.error('Unable to load user module permissions');
      return false;
    }
  }

  private isModulePermitted(route: string): boolean {
    if (LANDING_ROUTES.has(route)) return true;
    const moduleKey = ROUTE_MODULE_KEYS[route];
    return !!moduleKey && this.permissionsLoaded && this.userModuleAccess.canAccess(moduleKey);
  }

  private clearAccessCache(): void {
    this.authenticated = false;
    this.userEmail = '';
    this.userRole = null;
    this.allowedRoutes = [];
    this.permissionsLoaded = false;
    this.permissionsLoadPromise = null;
    this.userModuleAccess.clear();
  }
}
